import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { UsersApi as UserCoreApi } from '@api/users/users.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { ExportDialogComponent } from '@core/components/export-dialog/export-dialog.component';
import { ExportDialogComponentInjectedData, ExportDialogTypeEnum } from '@core/components/export-dialog/export-dialog.types';
import { User } from '@core/interfaces';
import { fuseAnimations } from '@fuse/animations';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DialogUsersComponent } from './dialogs/detail/dialog-users.component';
import { FilterUsersComponent } from './filter/filter-users.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { UsersApi } from './services/api/users-api.service';
import { UsersService } from './services/users-resolver.service';

@Component({
  selector: 'users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  animations: [fuseAnimations]
})
export class UsersComponent implements OnInit {
  @ViewChild('datatable', { read: ElementRef, static: true }) datatable: ElementRef<HTMLDivElement>;
  @ViewChild(FilterUsersComponent, { static: true }) filterUsersComponent: FilterUsersComponent;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  loading = true;
  currentUser: User;
  resultsLength = 0;
  users: User[] = [];
  totalCount: Number;
  filtered: Boolean = false;
  displayedColumns = ['name', 'email', 'type', 'mfaEnabled', 'loginProvider' , 'createdAt', 'lastLoginAt', 'edit'];

  private usersReload = new Subject<any>();
  private subscriptions = new Subject<void>();

  public hasEditUserPermission = this.authenticationService.hasPermission('users-edit');
  public hasCreateUserPermission = this.authenticationService.hasPermission('users-create');
  public hasCreateExportPermission = this.authenticationService.hasPermission('exports-create');

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private authenticationService: AuthenticationService,
    private progressBarService: FuseProgressBarService,
    private sidebarService: FuseSidebarService,
    private currentUserApi: UserCoreApi,
    private usersService: UsersService,
    private matDialog: MatDialog,
    private usersApi: UsersApi
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  async ngOnInit(): Promise<void> {
    this.sort.active = 'createdAt';
    this.sort.direction = 'desc';

    merge(
      this.sort.sortChange,
      this.paginator.page,
      this.filterUsersComponent.filterChanged.pipe(
        tap(() => this.paginator.firstPage())
      ),
      this.usersReload
    )
      .pipe(
        debounceTime(500),
        takeUntil(this.subscriptions),
        switchMap(() => {
          this.sort.direction = this.sort.direction || 'asc';
          const options = {
            orderBy: `${this.sort.active}_${this.sort.direction}`,
            offset: this.paginator.pageIndex * this.paginator.pageSize,
            limit: this.paginator.pageSize
          };

          this.loading = true;
          return this.usersService.loadData({ where: this.filterUsersComponent.filterChanged.value, options });

        })
      )
      .subscribe(async (result) => {
        this.datatable.nativeElement.scrollTo({
          top: 0,
        });

        this.currentUser = await this.currentUserApi.me();
        this.users = result.items;
        this.totalCount = result.totalCount;
        this.filtered = result.filtered;
        this.loading = false;
      });
  }

  toggleFilterUsersSidebar(): void {
    this.sidebarService.getSidebar('filterUsers').toggleOpen();
  }

  private async getUserById(userId?: string): Promise<User> {
    if (!userId) {
      return {
        name: null,
        email: null,
        groups: [],
        type: 'admin',
        role: 'default',
        archived: false,
        loginIntegrationEnabled: false
      };
    }

    return this.usersApi.getUserById(userId).toPromise();
  }

  public async openEditDialog(userId?: string): Promise<void> {
    this.progressBarService.show();

    const user = await this.getUserById(userId);
    this.progressBarService.hide();

    const result = await this.matDialog.open(DialogUsersComponent, {
      panelClass: 'dialog-users',
      data: {
        user,
        reloadPage: this.usersReload,
        isViewMode: user.name ? !this.hasEditUserPermission : false,
        currentUser: this.currentUser
      }
    })
      .afterClosed()
      .toPromise();

    if (result && result.reload) {
      this.reload();
    }
  }

  public openExportDialog(): void {
    this.matDialog.open(ExportDialogComponent, {
      panelClass: 'export-dialog',
      autoFocus: false,
      data: {
        type: ExportDialogTypeEnum.users,
        limit: 10000,
        total: this.totalCount,
        where: this.filterUsersComponent.filterChanged.value
      } as ExportDialogComponentInjectedData
    });
  }

  reload(): void {
    this.usersReload.next();
  }

  clearFilters(): void {
    this.filtered = false;
    this.filterUsersComponent.clearFilters();
  }
}
